<template>
  <div class="homepage-container contentful-container contentful-container-restricted wide">
    <suspense>
      <ContentfulRichText v-if="data" :content="data.content" :spacing-rule="data.spacingRule" :typography-rule="data.typeRule" />
    </suspense>
    <DiscountPopup v-if="discount" />
  </div>
</template>

<script>
import ContentfulRichText from '@/components/contentful/Fields/ContentfulRichText.vue';
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import StructuredOrganization from '@/components/meta/StructuredOrganization.js';
import buildSocialMetaTags from '@/components/meta/Social.js';
import { computed, defineAsyncComponent, ref, onMounted } from 'vue';
import useAsyncComputed from '@/compositions/useAsyncComputed.js';
import { useRouter } from 'vue-router';
import useHead from '@/compositions/useHead';
import useHome from '@/compositions/useHome';
import useCommon from '@/compositions/useCommon';
import useDiscount from "@/compositions/useDiscount";

export default {
  components: {
    ContentfulRichText,
    DiscountPopup: defineAsyncComponent(() => import('@/components/home/DiscountPopup.vue')),
  },
  props: contentfulProps,
  async setup(props, ctx) {
    const { data, resolveEntry, init } = useContentful('homepage', props, ctx, { resolveDepth: 10 });

    const [seoImage, loadingSeoImage, initSeoImage] = useAsyncComputed(async () => (await resolveEntry(data.value?.seoImage))?.fields?.file);

    const router = useRouter();

    const { user, ugcPixleeEnabled } = useCommon();

    const { updateShowProductListModal } = useHome();

    const { discount, processUrlDiscount } = useDiscount();

    const metaTags = computed(() => [
      {
        name: 'description',
        content:
          'Great style is easy. Article offers stylish modern, mid century, and scandinavian furniture from world renowned designers at accessible prices. Shop now.',
      },
      ...buildSocialMetaTags({
        title: data.value?.seoTitle,
        description: data.value?.seoDescription,
        url: import.meta.env.VITE_STOREFRONT_BASE_URL,
        image: seoImage.value,
      }),
    ]);

    useHead(
      {
        title: data.value?.seoTitle,
        script: [{ type: 'application/ld+json', children: JSON.stringify(StructuredOrganization) }],
        meta: computed(() => metaTags.value),
      },
      { appendTitleSuffix: false },
    );

    const discountName = router.currentRoute.value.params?.discount;
    if (discountName) {
      await processUrlDiscount({ discountName, campaignType: 'B2C' });
    }

    await Promise.all([init, initSeoImage]);

    return {
      data,
      discount,
      updateShowProductListModal,
    };
  },
};
</script>
